// Who sent this RF code? — one answer for the receiver card, the Message Box
// and the RF screens.
//
// ── The RF number: the sensor's own identity ───────────────────────────────
// A sensor is shown and searched as `RF-000042`, which is its RfDevice row id.
//
// ⚠️ IT IS NOT THE TRANSMITTER ID. The transmitter id (top 24 bits of the code)
// is burned into the hardware by its factory, so it changes when a unit is
// replaced — and then the sensor's name, room, recipients, history and switch
// actions would all be stranded on the old number. The RF number belongs to
// the SENSOR: assigned once, never reused (AUTO_INCREMENT), and kept across a
// hardware swap (`ReplaceRfTransmitter`). The transmitter id is still shown,
// smaller, as `hw 3344130` — it is what the radio actually says.
//
// ⚠️ "RF-" IS NOT DECORATION. A bare number reads as a device UniqueNumber, and
// an RF sensor is never a DevicesInstalled row (docs/17); the prefix keeps the
// two namespaces visibly apart.
//
// ── Describing a code ─────────────────────────────────────────────────────
// Three states, because they mean different things to an installer:
//   named    a configured sensor — say its name, type, room and the meaning
//   unnamed  the server has a row (it heard it) but nobody has named it
//   new      never seen at all
// A meaning nobody labelled is GUESSED from the signature library
// (rfSignatures.js) and always marked as a guess.

import { transmitterOf, eventByteOf } from './rfCodes';
import { rfType } from './rfTypes';
import { detectRfType } from './rfSignatures';

/** A sensor's display name: its Name, or "Unassigned · <hw id>" when it has none. */
export const rfSensorName = (d) => String(d.Name || '').trim() || `Unassigned · ${d.TransmitterId}`;

/** RfDeviceId -> 'RF-000042', or '' for none. */
export function rfNumber(rfDeviceId) {
  const n = Math.trunc(Number(rfDeviceId));
  return Number.isFinite(n) && n > 0 ? `RF-${String(n).padStart(6, '0')}` : '';
}

/** 'RF-000042' / 'rf42' / 'RF 42' -> 42, anything else -> 0. */
export function parseRfNumber(text) {
  const m = /^\s*rf[-\s]?0*(\d+)\s*$/i.exec(String(text || ''));
  return m ? Number(m[1]) : 0;
}

/**
 * Index GetRfDevices rows for lookup.
 * -> { byCode: Map<string, {device, code}>, byTransmitter: Map<number, device>, byId: Map<number, device> }
 */
export function buildRfDirectory(devices) {
  const byCode = new Map();
  const byTransmitter = new Map();
  const byId = new Map();
  (devices || []).forEach((d) => {
    if (!d) return;
    const t = Number(d.TransmitterId);
    if (t) byTransmitter.set(t, d);
    if (Number(d.RfDeviceId)) byId.set(Number(d.RfDeviceId), d);
    (d.Codes || []).forEach((c) => byCode.set(String(c.REMOTEVALUE), { device: d, code: c }));
  });
  return { byCode, byTransmitter, byId };
}

/**
 * Everything known about one code.
 *
 * @param dir  buildRfDirectory() result (may be null while loading)
 * @param sig  rfSignatures buildSignatures() result (optional)
 */
export function describeRfCode(rawCode, dir, sig) {
  const code = Number(rawCode);
  const transmitterId = transmitterOf(code);
  const eventByte = eventByteOf(code);
  const hitCode = dir?.byCode?.get(String(code)) || null;
  const device = (hitCode && hitCode.device) || dir?.byTransmitter?.get(transmitterId) || null;
  const name = String(device?.Name || '').trim();
  const status = !device ? 'new' : (name ? 'named' : 'unnamed');

  const typeKey = String(device?.RfType || '').toLowerCase();
  const hasType = !!typeKey && typeKey !== 'other';

  // The meaning: as labelled on the code row, else a guess from the library.
  let meaning = String(hitCode?.code?.DeviceAction || '').trim();
  let meaningGuessed = false;
  let guessType = '';
  if (!meaning && sig) {
    if (hasType) {
      const hit = sig.byType?.get(typeKey)?.get(eventByte);
      if (hit && hit.meaning && !hit.conflict) { meaning = hit.meaning; meaningGuessed = true; }
    } else {
      const det = detectRfType([eventByte], sig);
      if (det) {
        guessType = det.rfType;
        const m = det.meanings.get(eventByte);
        if (m && m.meaning) { meaning = m.meaning; meaningGuessed = true; }
      }
    }
  }

  const t = rfType(hasType ? typeKey : (guessType || 'other'));
  return {
    code,
    transmitterId,
    eventByte,
    status,
    rfDeviceId: Number(device?.RfDeviceId) || 0,
    rfNumber: rfNumber(device?.RfDeviceId),
    name,
    rfType: hasType ? typeKey : '',
    guessType: hasType ? '' : guessType,
    typeLabel: t.label,
    icon: t.icon,
    roomName: String(device?.RoomName || '').trim(),
    locationName: String(device?.LocationName || '').trim(),
    notify: !!Number(device?.Notify),
    meaning,
    meaningGuessed,
    device,
  };
}

/** The meaning as text, marked when it is a guess. */
export const meaningText = (d) => (d.meaning ? (d.meaningGuessed ? `probably ${d.meaning}` : d.meaning) : `#${d.eventByte}`);

/**
 * One line, for a log:
 *   🚪 Main door (Door sensor) · Open · RF-000042 · hw 3344130 #9 · Office · notify on
 *   📡 Unnamed sensor RF-000057 · hw 3344130 #9 · probably Door sensor — probably Open
 *   🆕 New sensor · hw 3344130 #9 · probably Door sensor — probably Open
 */
export function rfSummary(d) {
  if (!d) return '';
  const hw = `hw ${d.transmitterId} #${d.eventByte}`;
  const guess = d.guessType ? ` · probably ${d.typeLabel}` : '';
  if (d.status === 'named') {
    return [
      `${d.icon} ${d.name} (${d.typeLabel})`,
      meaningText(d),
      d.rfNumber,
      hw,
      d.roomName || d.locationName,
      d.notify ? 'notify on' : 'notify off',
    ].filter(Boolean).join(' · ');
  }
  if (d.status === 'unnamed') {
    return [`📡 Unnamed sensor ${d.rfNumber}`, hw, `${meaningText(d)}${guess}`].join(' · ');
  }
  return [`🆕 New sensor`, hw, `${meaningText(d)}${guess}`].join(' · ');
}

// ── The Message Box line for an RfV (owner, 2026-10-13: "short, nothing repeated") ──
// The raw line said the code three ways (decimal, the binary, hw+#event), the
// receiver's number twice, and "Door Sensor (Door sensor)" under a 🚪. Now:
//   🚪 Bedroom Door Sensor: Close · Sanjay Bedroom · via RF RECEIVER · 🔕
// and every technical fact ONCE, in rfDetailLine (hover / Copy). Search still
// runs on the raw text and on rfMatches, so nothing hidden stops being findable.

/** "RF RECEIVER" out of a console caption "Owner - Location/Room/RF RECEIVER". */
export function receiverNameOf(label, uno = '') {
  const path = String(label || '').split(' - ').pop();
  const name = path.split('/').pop().trim();
  return name || String(uno || '');
}

const typeIsInName = (d) => {
  const word = String(d.typeLabel || '').split(/\s+/)[0].toLowerCase();
  return !!word && String(d.name || '').toLowerCase().includes(word);
};

/** One short line. receiverLabel = the console caption of the receiver. */
export function rfShortLine(d, receiverLabel = '', receiverUno = '') {
  if (!d) return '';
  const via = `via ${receiverNameOf(receiverLabel, receiverUno)}`;
  if (d.status === 'named') {
    const who = typeIsInName(d) ? d.name : `${d.name} (${d.typeLabel})`;
    return [
      `${d.icon} ${who}: ${meaningText(d)}`,
      d.roomName || d.locationName,
      via,
      d.notify ? '🔔' : '🔕',
    ].filter(Boolean).join(' · ');
  }
  const guess = d.guessType ? `probably ${d.typeLabel}, ` : '';
  const head = d.status === 'unnamed' ? `📡 Unnamed sensor ${d.rfNumber}` : '🆕 New sensor';
  return [`${head}: ${guess}${meaningText(d)}`, via].join(' · ');
}

/** The technical facts, each once: "RF-000040 · code 13586553 = hw 53072 + event #121 · receiver 875552 · notify off". */
export function rfDetailLine(d, receiverUno = '') {
  if (!d) return '';
  return [
    d.rfNumber,
    `code ${d.code} = hw ${d.transmitterId} + event #${d.eventByte}`,
    receiverUno ? `receiver ${receiverUno}` : '',
    d.status === 'named' ? (d.notify ? 'notify on' : 'notify off') : '',
  ].filter(Boolean).join(' · ');
}

/**
 * The line UNDER the raw RfV line in the Message Box (owner, 2026-10-15: "all
 * message should be once"). The raw line above already shows the receiver, its
 * name and the decimal code, so this carries only what it does not:
 *   🚪 Bedroom Door Sensor: Close · Sanjay Bedroom · RF-000040 · hw 53072 + event #121 · 🔕
 *   📡 Unnamed sensor RF-000061: probably Door sensor, probably Open · hw 45334 + event #115
 */
export function rfUnderLine(d) {
  if (!d) return '';
  const hw = `hw ${d.transmitterId} + event #${d.eventByte}`;
  if (d.status === 'named') {
    const who = typeIsInName(d) ? d.name : `${d.name} (${d.typeLabel})`;
    return [
      `${d.icon} ${who}: ${meaningText(d)}`,
      d.roomName || d.locationName,
      d.rfNumber,
      hw,
      d.notify ? '🔔' : '🔕',
    ].filter(Boolean).join(' · ');
  }
  const guess = d.guessType ? `probably ${d.typeLabel}, ` : '';
  const head = d.status === 'unnamed' ? `📡 Unnamed sensor ${d.rfNumber}` : '🆕 New sensor';
  return [`${head}: ${guess}${meaningText(d)}`, hw].join(' · ');
}

/** Does this description match a search? RF number, hw id, name, room, code. */
export function rfMatches(d, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q || !d) return false;
  const n = parseRfNumber(q);
  if (n && n === d.rfDeviceId) return true;
  return [d.rfNumber, String(d.transmitterId), String(d.code), d.name, d.roomName, d.typeLabel]
    .some((s) => String(s || '').toLowerCase().includes(q));
}
